import { useCallback, useEffect, useMemo, useReducer } from "react";
import { saveAs } from "file-saver";
import { useFetcherData } from "v2/hooks/useFetcherData";
import { FetcherKeys } from "routes/dataroutes/Fetchers";
import { transformInvoices } from "v2/pages/billing/tabs/billingDataTransformers";
import { PaginatedInvoicesResponse, TransformedInvoice } from "v2/pages/billing/types";
import scraperApi from "api";
import Toaster from "components/Toaster";

export interface InvoiceLineItem {
  id: string;
  description?: string;
  amount: number;
  quantity?: number;
  unit_amount?: number;
  entity_type?: string;
  entity_id?: string;
}

export interface Invoice {
  id: string;
  date: number; // Unix timestamp
  due_date?: number;
  status: string;
  currency_code: string;
  total: number;
  amount_due: number;
  amount_paid: number;
  paid_at?: number;
  sub_total: number;
  tax: number;
  line_items?: InvoiceLineItem[];
  number?: string;
  pdf_download_url?: string;
}

export type InvoicesLoaderDataType = { invoices: Invoice[] | null};


// ============================================================================
// Paginated variant — server-side page-number pagination + status filtering.
// Used by PaymentHistoryCard.
//
// The backend handles Chargebee's cursor complexity and returns page-number
// based results with a next-page lookahead. The frontend caches the prefetched
// next page for instant forward navigation.
// ============================================================================

export type InvoiceStatusFilter = "all" | "paid" | "unpaid" | "voided";

const INVOICES_PAGE_SIZE = 10;

export type PaginatedInvoicesLoaderDataType = {
  invoices: PaginatedInvoicesResponse | null;
};

// ── Reducer ─────────────────────────────────────────────────────────────

type PaginationState = {
  currentPage: number;
  statusFilter: InvoiceStatusFilter;
  prevHref: string | null;
  pageCache: Map<number, TransformedInvoice[]>; // all visited + prefetched pages
  cachedInvoices: TransformedInvoice[] | null;   // instant display from cache
  isPageTransitioning: boolean;
  hasLoadedOnce: boolean;
};

type PaginationAction =
  | { type: "GO_TO_PAGE"; page: number; cache: Map<number, TransformedInvoice[]> }
  | { type: "SET_FILTER"; status: InvoiceStatusFilter }
  | { type: "HREF_SYNCED"; href: string }
  | { type: "RESPONSE_RECEIVED"; page: number; currentPageInvoices: TransformedInvoice[]; nextPageInvoices: TransformedInvoice[] }
  | { type: "ERROR_RECEIVED" };

const initialState: PaginationState = {
  currentPage: 1,
  statusFilter: "all",
  prevHref: null,
  pageCache: new Map(),
  cachedInvoices: null,
  isPageTransitioning: false,
  hasLoadedOnce: false,
};

function paginationReducer(state: PaginationState, action: PaginationAction): PaginationState {
  switch (action.type) {
    case "GO_TO_PAGE": {
      const cached = action.cache.get(action.page) ?? null;
      return {
        ...state,
        currentPage: action.page,
        cachedInvoices: cached,
        isPageTransitioning: !cached,
      };
    }

    case "SET_FILTER":
      return {
        ...state,
        statusFilter: action.status,
        currentPage: 1,
        pageCache: new Map(),
        cachedInvoices: null,
        isPageTransitioning: true,
      };

    case "HREF_SYNCED":
      return { ...state, prevHref: action.href };

    case "RESPONSE_RECEIVED": {
      const cache = new Map(state.pageCache);
      // Cache the current page we just fetched
      cache.set(action.page, action.currentPageInvoices);
      // Cache the prefetched next page (lookahead)
      if (action.nextPageInvoices.length > 0) {
        cache.set(action.page + 1, action.nextPageInvoices);
      }
      return {
        ...state,
        cachedInvoices: null,
        isPageTransitioning: false,
        hasLoadedOnce: true,
        pageCache: cache,
      };
    }

    case "ERROR_RECEIVED":
      return { ...state, isPageTransitioning: false };

    default:
      return state;
  }
}

// ── Helper ──────────────────────────────────────────────────────────────

function buildInvoicesFetcherHref(page: number, statusFilter: InvoiceStatusFilter): string {
  const params = new URLSearchParams();
  params.set("page", String(page));
  params.set("pageSize", String(INVOICES_PAGE_SIZE));
  // "posted" = finalized invoice awaiting collection; treat it as unpaid so it
  // shows under both "All" and "Unpaid" (previously omitted, hiding those invoices).
  const statusParam =
    statusFilter === "all"    ? "paid,not_paid,payment_due,voided,posted" :
    statusFilter === "unpaid" ? "not_paid,payment_due,posted" :
    statusFilter;
  params.set("status", statusParam);
  return `/billing-data/invoices-paginated?${params.toString()}`;
}

// ── Hook ────────────────────────────────────────────────────────────────

/**
 * Paginated invoices hook with server-side page-number pagination.
 *
 * The backend fetches the requested page plus a lookahead of the next page.
 * We cache the lookahead so clicking "next" shows data instantly, then
 * prefetch the page after that in the background.
 */
export function useInvoicesPaginated() {
  const [state, dispatch] = useReducer(paginationReducer, initialState);
  const { currentPage, statusFilter, prevHref, cachedInvoices, isPageTransitioning, hasLoadedOnce, pageCache } = state;

  const fetcherHref = useMemo(
    () => buildInvoicesFetcherHref(currentPage, statusFilter),
    [currentPage, statusFilter],
  );

  const {
    value: paginatedResponse,
    error,
    reload,
    fetcher,
  } = useFetcherData<PaginatedInvoicesLoaderDataType>({
    fetcherKey: FetcherKeys.INVOICES_PAGINATED,
    fetcherHref,
    performInitialFetch: true,
  });

  // Always fetch on page/filter change — the response updates the cache
  // with the next-page lookahead. Cached pages render instantly via the
  // reducer (GO_TO_PAGE), so the fetch runs silently in the background.
  useEffect(() => {
    if (prevHref !== null && prevHref !== fetcherHref) {
      reload(false);
    }
    dispatch({ type: "HREF_SYNCED", href: fetcherHref });
  }, [fetcherHref, prevHref, reload]);

  // Process successful response — cache lookahead, clear transition
  useEffect(() => {
    if (paginatedResponse?.invoices && paginatedResponse.invoices.page === currentPage) {
      dispatch({
        type: "RESPONSE_RECEIVED",
        page: currentPage,
        currentPageInvoices: transformInvoices(paginatedResponse.invoices.currentPage),
        nextPageInvoices: paginatedResponse.invoices.nextPage.length > 0
          ? transformInvoices(paginatedResponse.invoices.nextPage)
          : [],
      });
    }
  }, [paginatedResponse, currentPage]);

  // Show toast on fetch failure
  useEffect(() => {
    if (error && hasLoadedOnce) {
      dispatch({ type: "ERROR_RECEIVED" });
      Toaster.error("Failed to load invoices. Please try again.");
    }
  }, [error, hasLoadedOnce]);

  // ── Derived state ───────────────────────────────────────────────────

  const invoices = useMemo(() => {
    if (cachedInvoices) return cachedInvoices;
    // Only render from the response if it matches the current page AND the current
    // href — the page check alone isn't enough on filter change, since both the old
    // and new responses are at page 1.
    if (paginatedResponse?.invoices?.page === currentPage && fetcherHref === prevHref) {
      return transformInvoices(paginatedResponse.invoices.currentPage);
    }
    return pageCache.get(currentPage) ?? [];
  }, [paginatedResponse, cachedInvoices, currentPage, pageCache, fetcherHref, prevHref]);

  const isFetching = (!paginatedResponse && !error && !cachedInvoices) || fetcher?.state !== "idle";
  const isInitialLoad = isFetching && !hasLoadedOnce;
  const isPaginating = isPageTransitioning && hasLoadedOnce;

  const responseMatchesCurrentState = paginatedResponse?.invoices?.page === currentPage && fetcherHref === prevHref;
  const hasNextPage = responseMatchesCurrentState
    ? paginatedResponse.invoices!.hasMore
    : pageCache.has(currentPage + 1); // cached lookahead = next page exists
  const hasPrevPage = currentPage > 1;

  // ── Actions ─────────────────────────────────────────────────────────

  const goToPage = useCallback((page: number) => {
    if (page < 1) return;
    dispatch({ type: "GO_TO_PAGE", page, cache: pageCache });
  }, [pageCache]);

  const setStatusFilter = useCallback((status: InvoiceStatusFilter) => {
    dispatch({ type: "SET_FILTER", status });
  }, []);

  const retry = useCallback(() => reload(true), [reload]);

  const downloadInvoice = useCallback(async (invoiceId: string) => {
    const invoice = invoices.find((inv) => inv.id === invoiceId);

    if (!invoice) {
      Toaster.error("Invoice not found");
      return;
    }

    try {
      const response = await scraperApi.billing.invoices.getInvoicePdf(invoiceId);

      if (response.valid_till && Date.now() / 1000 > response.valid_till) {
        throw new Error("Download link has expired");
      }

      const pdfResponse = await fetch(response.download_url);
      if (!pdfResponse.ok) throw new Error("Failed to download PDF");

      const blob = await pdfResponse.blob();
      saveAs(blob, `invoice-${invoice.invoiceNumber}.pdf`);
    } catch {
      Toaster.error("Failed to download invoice. Please try again.");
    }
  }, [invoices]);

  return {
    invoices,
    currentPage,
    hasNextPage,
    hasPrevPage,
    goToPage,
    statusFilter,
    setStatusFilter,
    isInitialLoad,
    isPaginating,
    error,
    retry,
    downloadInvoice,
  };
}
